/**
 * @typedef {Object} Props
 * @property {Object} selectedMetric
 * @property {Scale} selectedScale
 * @property {Benchmark} selectedBenchmark
 * @property {Class} selectedClass
 * @property {*} selectedScreeningWindow
 * @property {*} selectedStatePercentileCuts
 * @property {Student[]} students
 * @property {Array} benchmarks
 * @property {Student} selectedStudent
 * @property {*} selectedEquating
 * @property {*} selectedLocale
 * @property {Object} selectedSchool
 * @property {Object} params
 * @property {*} progressActivities
 * @property {*} customBenchmarks
 * @property {*} reportConfig
 * @property {*} dyslexiaReport
 * @property {Function} clearReport
 * @property {Function} fetchTrackingReport
 * @property {Function} fetchParentReport
 * @property {Function} fetchProgressReport
 * @property {Function} scaleProgressReport
 * @property {Function} fetchDiagnoseReport
 * @property {Function} fetchDyslexiaReport
 * @property {Function} scaleDyslexiaReport
 * @property {Function} fetchReadingRopeReport
 * @property {Function} fetchSkillsDiagnosticReport
 */

/**
 * Gets report data based on the provided parameters
 * @param {boolean} fetch
 * @param {boolean} scale
 * @param {Params} params
 * @param {Props} props
 * @param {boolean} hasFetched
 * @param {Function} setHasFetched
 */

const getReportData = (
  fetch,
  scale,
  params,
  props,
  hasFetched,
  setHasFetched
) => {
  //if valid selections
  let {
    selectedMetric,
    selectedScale,
    selectedBenchmark,
    selectedClass,
    selectedScreeningWindow,
    selectedStatePercentileCuts,
    selectedDistrict,
  } = props;

  if (
    !(selectedMetric && selectedScale && selectedBenchmark && selectedClass) ||
    isEmpty(selectedBenchmark) ||
    isEmpty(selectedScale) ||
    isEmpty(selectedMetric) ||
    (props.students.length > 0 && props.benchmarks.length == 0)
  ) {
    return;
    // TODO check for !isAggregate instead
  } else if (
    props.params.report == "progress" ||
    props.params.report == "parent" ||
    props.params.report == "diagnostic"
  ) {
    if (!props.selectedStudent) {
      return;
    }
  } else if (
    (props.params.report != "dyslexiarisk" &&
      selectedMetric &&
      selectedMetric.value == "DRI") ||
    (props.params.report == "dyslexia" &&
      selectedMetric &&
      selectedMetric.value != "DRI")
  ) {
    props.clearReport(props.params.report);
    return;
  }

  // If this isn't an ORF report, don't fetch equated activities
  // TODO, deprecate this once additional metrics are equated
  let equated = props.selectedEquating;
  let locale = props.selectedLocale;

  // If we haven't fetched a report since the component mounted, fetch one
  if (!hasFetched) {
    fetch = true;
    scale = false;
    setHasFetched(true);
  }

  // TODO clean up redux so that this check isn't necessary
  let classroomId = props.selectedClass.value
    ? props.selectedClass.value
    : props.selectedClass.ui.value;
  const schoolId = props.selectedSchool.id;

  // based on the currently active report
  // TODO simplify this to two lines of code for all reports (perhaps by storng the individual methods in an object)
  switch (props.params.report) {
    case "tracking": // Load Tracking report
      if (fetch) {
        props.fetchTrackingReport(
          schoolId,
          props.students,
          selectedScreeningWindow,
          selectedDistrict,
          params
        );
      }
      break;
    // TODO enable this data fetching when the corresponding report is turned on
    case "parent":
      // Note: fetch and scale progress report, since it's a subset of the parent report
      if (fetch) {
        props.fetchParentReport(
          props.selectedStudent,
          props.selectedMetric,
          props.selectedBenchmark,
          props.customBenchmarks,
          props.reportConfig,
          equated,
          locale
        );
        props.fetchProgressReport(
          props.selectedStudent,
          selectedMetric.value,
          selectedScale.value,
          equated,
          locale,
          { skipARM: true },
        );
      } else if (scale) {
        props.scaleProgressReport(
          props.progressActivities,
          selectedMetric.value,
          selectedScale.value,
          selectedBenchmark,
          props.selectedStudent,
          props.customBenchmarks,
          locale,
          selectedStatePercentileCuts
        );
      }
      break;
    case "progress": // Load/Scale Progress report
      // TODO integrate student picker
      if (fetch) {
        props.fetchProgressReport(
          props.selectedStudent,
          selectedMetric.value,
          selectedScale.value,
          equated,
          locale,
          {},
        );
      } else if (scale) {
        props.scaleProgressReport(
          props.progressActivities,
          selectedMetric.value,
          selectedScale.value,
          selectedBenchmark,
          props.selectedStudent,
          props.customBenchmarks,
          locale,
          selectedStatePercentileCuts
        );
      }
      break;
    case "diagnostic": // Load Diagnostic report
      fetch && props.fetchDiagnoseReport(props.selectedStudent, locale);
      break;
    case "dyslexiarisk": // Load/Scale Dyslexia Risk report
      if (fetch) {
        props.fetchDyslexiaReport(classroomId, selectedBenchmark, locale);
      } else if (scale) {
        props.scaleDyslexiaReport(
          props.dyslexiaReport,
          selectedBenchmark,
          locale
        );
      }
      break;
    case "readingrope":
      if (fetch)
        props.fetchReadingRopeReport(
          classroomId,
          props.selectedStudent,
          selectedStatePercentileCuts
        );
      break;
    case "skillsstatus":
      // data fetch is handled within the report component itself for pagination reasons.
      break;
    case "skillsdiagnostic":
      if (fetch) props.fetchSkillsDiagnosticReport(props.selectedStudent);
      break;
    case "badging":
      // data fetch is handled within the report component
      break;
    case "classprogress":
      // data fetch is handled within the report component
      break;
    default:
      console.log("invalid report type: ", props.params);
      break;
  }
};

function isEmpty(obj) {
  for (var key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
}

export default getReportData;
